import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Loader2,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  RotateCw,
  Disc3,
  Timer,
  Unplug,
  Check,
  X,
  CheckCircle2,
  XCircle,
  Gauge,
  Activity,
  Download,
  Upload,
  FileClock,
  Square,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import { formatApiError, formatMac } from "./Manufacturing/constants";
import { parseFactoryLine, parseFactoryLines } from "utils/factoryTest";
import { buildExportResult } from "utils/wearableExport";
import { fwAtLeast } from "utils/wearableStatus";
import {
  initCentrifugeState,
  reduceCentrifugeState,
  parseDumpPeak,
} from "utils/centrifuge";
import {
  buildCentrifugeFile,
  parseCentrifugeFile,
  centrifugeFileName,
  wearableShortName,
} from "utils/centrifugeFile";
import { saveTextFile, openTextFile } from "utils/fileDialog";
import WaveformGraph from "./WaveformGraph";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const normMac = (v) =>
  String(v || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

// Firmware with the resyncable centrifuge protocol: centstatus snapshots,
// `centrifuge start [seconds]`, fatal prepare_failed.
const CENT2_FW_MIN = [0, 5, 82];

const ARM_CHOICES = [5, 10, 30];

const fmtClock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const btnSecondary =
  "rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50";

/**
 * Untethered centrifuge validation, rebuilt around the v0.5.82 resyncable
 * protocol. One `capture` step whose UI is driven ENTIRELY by the device's
 * reported phase (entering → preparing → waiting → countdown → recording),
 * so the screen can never disagree with the hardware: every phase edge is a
 * guaranteed event, every wait has a timeout and an exit, and a reconnect
 * heals from one `centstatus` snapshot. Results land on the same screen
 * whether they come from a live Finish or a later read of the flash.
 */
const CentrifugeFlow = ({
  wearable,
  bleMac,
  deviceState,
  deviceRecords,
  deviceFw,
  send,
  kick,
  receivedData,
  connected,
  commandsLocked,
  exportRecords,
  exportReceived,
  exportTotal,
  onExit,
}) => {
  const { t } = useTranslation();
  // step: "home" | "capture" | "read"  (imported overlays everything)
  const [step, setStep] = useState("home");
  const [state, setState] = useState(initCentrifugeState);
  const [runId, setRunId] = useState(0);
  const scanPosRef = useRef(receivedData.length);

  // Always-fresh view of the stream for the async loops.
  const receivedRef = useRef(receivedData);
  useEffect(() => {
    receivedRef.current = receivedData;
  }, [receivedData]);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Device identity for logging.
  const [mac, setMac] = useState(bleMac ? normMac(bleMac) : null);
  const macRef = useRef(mac);
  useEffect(() => {
    macRef.current = mac;
  }, [mac]);
  const idSentRef = useRef(false);

  // Result state — one shape for both sources (live stop / flash read).
  const [reading, setReading] = useState(false);
  const [reader, setReader] = useState(null); // { peakG, records, source, fault } | { empty:true }
  const [readError, setReadError] = useState(null);

  // Logging (opt-in).
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [regError, setRegError] = useState(null);
  const [showLog, setShowLog] = useState(false);

  // Waveform graph (on-demand full export). NOTE: a clean export auto-exits
  // the device to IDLE (firmware behaviour), so once graphed, "Run again"
  // becomes "New capture" (full re-entry + wipe).
  const [graphing, setGraphing] = useState(false);
  const [graphRecords, setGraphRecords] = useState(null);
  const [graphError, setGraphError] = useState(null);

  const [saveBusy, setSaveBusy] = useState(false);
  const [imported, setImported] = useState(null);

  // Arm / finish command confirmation.
  const [armSecs, setArmSecs] = useState(30);
  const [armPending, setArmPending] = useState(false); // sent start, awaiting countdown
  const [armStuck, setArmStuck] = useState(false); // no phase edge came back
  const [finishing, setFinishing] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);

  const cent2 = fwAtLeast(deviceFw || state.fw, CENT2_FW_MIN);

  // ── Passive scanner ─────────────────────────────────────────────────
  // Folds every new line into the reducer. The cursor self-heals after a
  // replug (receivedData restarts at 0 on reconnect — without the guard the
  // scanner is blind until the new buffer outgrows the old one).
  useEffect(() => {
    if (scanPosRef.current > receivedData.length) scanPosRef.current = 0;
    if (receivedData.length <= scanPosRef.current) return;
    const events = [];
    for (let i = scanPosRef.current; i < receivedData.length; i++) {
      for (const ev of parseFactoryLines(receivedData[i]?.data)) {
        if (ev.t === "id" && ev.mac && !macRef.current) setMac(normMac(ev.mac));
        events.push(ev);
      }
    }
    scanPosRef.current = receivedData.length;
    if (events.length) {
      setState((prev) => events.reduce(reduceCentrifugeState, prev));
    }
  }, [receivedData]);

  // Resolve a MAC via `id` if we weren't given one (needed to log).
  useEffect(() => {
    if (mac || !connected || idSentRef.current) return;
    idSentRef.current = true;
    (async () => {
      await send("id").catch(() => {});
      for (let i = 0; i < 4; i++) {
        await sleep(1500);
        if (macRef.current || !aliveRef.current) break;
        await kick?.().catch?.(() => {});
      }
    })();
    // eslint-disable-next-line
  }, [mac, connected]);

  // Send a command reliably (the console needs a DTR edge to read it).
  const sendCmd = useCallback(
    async (cmd) => {
      await send(cmd).catch(() => {});
      await sleep(150);
      await kick?.().catch?.(() => {});
    },
    [send, kick]
  );

  const inMode =
    deviceState === "CENTRIFUGE" || (state.entered && !state.ended);

  // Leave the flow. Sends `idle` (with a kick — a deaf console would other-
  // wise leave the device stuck in the mode) unless the operator explicitly
  // keeps the device armed for an unplugged spin.
  const exit = ({ keepArmed = false } = {}) => {
    if (!keepArmed && inMode) {
      sendCmd("idle");
    }
    onExit();
  };

  // ── Enter the mode ──────────────────────────────────────────────────
  const startCapture = () => {
    scanPosRef.current = receivedRef.current.length;
    setState(initCentrifugeState());
    setReader(null);
    setReadError(null);
    setGraphRecords(null);
    setGraphError(null);
    setArmPending(false);
    setArmStuck(false);
    setConfirmExit(false);
    setRunId((n) => n + 1);
    setStep("capture");
  };

  const enteredRef = useRef(false);
  const nackRef = useRef(false);
  useEffect(() => {
    enteredRef.current = state.entered;
  }, [state.entered]);
  useEffect(() => {
    nackRef.current = !!state.nack;
  }, [state.nack]);

  useEffect(() => {
    if (step !== "capture" || runId === 0) return undefined;
    let cancelled = false;
    (async () => {
      await sleep(60); // StrictMode-safe: cancel a double-mount before it sends
      if (cancelled) return;
      await send("centrifuge ui").catch(() => {});
      for (let i = 0; i < 8 && !cancelled; i++) {
        await sleep(1800);
        if (cancelled || enteredRef.current || nackRef.current) break;
        await kick?.().catch?.(() => {});
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [step, runId]);

  // Resync: the device was already mid-capture when the flow opened (UI
  // reopened, or replugged during a run) → jump straight to the live screen
  // and let a centstatus snapshot rebuild everything.
  const resyncedRef = useRef(false);
  useEffect(() => {
    if (resyncedRef.current) return;
    if (step === "home" && deviceState === "CENTRIFUGE") {
      resyncedRef.current = true;
      setState((prev) => ({ ...prev, entered: true }));
      setStep("capture");
      sendCmd("centstatus");
    }
    // eslint-disable-next-line
  }, [step, deviceState]);

  // Resync after a replug while capturing: the modal's handshake re-runs
  // `ui on`; once the console answers, one centstatus heals the reducer.
  const wasConnectedRef = useRef(connected);
  useEffect(() => {
    const was = wasConnectedRef.current;
    wasConnectedRef.current = connected;
    if (!was && connected && step === "capture") {
      (async () => {
        await sleep(2500); // let the modal's ui-on handshake win the console
        if (aliveRef.current) await sendCmd("centstatus");
      })();
    }
    // eslint-disable-next-line
  }, [connected, step]);

  // ── Arm / cancel / finish ───────────────────────────────────────────
  const arm = async () => {
    // A stale refusal from an earlier attempt must not cancel this one.
    setState((prev) => ({ ...prev, ignored: null, nack: null }));
    setArmPending(true);
    setArmStuck(false);
    await sendCmd(cent2 ? `centrifuge start ${armSecs}` : "centrifuge start");
  };

  // Arm confirmation: a phase edge (countdown) or a refusal must arrive —
  // "no answer" gets surfaced instead of letting the operator unplug a
  // device that never armed.
  useEffect(() => {
    if (!armPending) return undefined;
    if (state.phase === "countdown") {
      setArmPending(false);
      return undefined;
    }
    if (state.ignored || state.nack) {
      setArmPending(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setArmPending(false);
      setArmStuck(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [armPending, state.phase, state.ignored, state.nack]);

  const cancelCountdown = () => sendCmd("centrifuge stop");

  const finishRun = async () => {
    setFinishing(true);
    await sendCmd("centrifuge stop");
  };

  // A stopped verdict (live Finish, physical button, store-full, or one
  // recovered from a centstatus last-run after a replug) becomes the result.
  useEffect(() => {
    if (step !== "capture") return;
    const s = state.stopped
      ? {
          peakG:
            state.stopped.peakG ??
            (state.stopped.peakMg != null ? state.stopped.peakMg / 1000 : null),
          records: state.stopped.records,
          fault: state.stopped.fault,
          source: "stopped",
        }
      : state.lastRun
      ? {
          peakG:
            state.lastRun.peakMg != null ? state.lastRun.peakMg / 1000 : null,
          records: state.lastRun.records,
          fault: state.lastRun.fault,
          source: "stopped",
        }
      : null;
    if (s && state.phase === "waiting") {
      setFinishing(false);
      setReader(s);
      setStep("read");
    }
  }, [step, state.stopped, state.lastRun, state.phase]);

  // ── Entering / countdown / recording local clocks ──────────────────
  const [, forceTick] = useState(0);
  const phaseUi = !state.entered
    ? state.nack
      ? "nack"
      : state.error
      ? "error"
      : "entering"
    : state.error && state.prepFailed
    ? "error"
    : state.phase || "entering";

  const clockActive =
    step === "capture" &&
    (phaseUi === "countdown" ||
      phaseUi === "recording" ||
      phaseUi === "entering" ||
      phaseUi === "preparing");
  useEffect(() => {
    if (!clockActive) return undefined;
    const iv = setInterval(() => forceTick((n) => n + 1), 250);
    return () => clearInterval(iv);
  }, [clockActive]);

  // Interpolate between (lossy) ticks so the numerals move smoothly.
  const remainRef = useRef(null); // { ms, at }
  useEffect(() => {
    if (state.remainingMs != null) {
      remainRef.current = { ms: state.remainingMs, at: Date.now() };
    } else if (phaseUi !== "countdown") {
      remainRef.current = null;
    }
  }, [state.remainingMs, phaseUi]);
  const armMs = state.arm?.armMs ?? 30000;
  const remainMs = remainRef.current
    ? Math.max(0, remainRef.current.ms - (Date.now() - remainRef.current.at))
    : armMs;

  const elapsedRef = useRef(null); // { ms, at }
  useEffect(() => {
    // The per-second `recording` ticks are the live source; a cent_status
    // snapshot only fires on phase edges (so its elapsed_ms is 0 at the
    // recording edge) and after a resync. Take whichever is FURTHEST along,
    // and never move the clock backwards — the old `run ?? recording` order
    // pinned the display to the edge snapshot's 0 forever.
    const tick = state.recording?.elapsedMs;
    const snap = state.run?.elapsedMs;
    const ms =
      tick != null || snap != null ? Math.max(tick ?? 0, snap ?? 0) : null;
    if (phaseUi !== "recording") {
      elapsedRef.current = null;
    } else if (ms != null) {
      const cur = elapsedRef.current;
      const shown = cur ? cur.ms + (Date.now() - cur.at) : -1;
      if (ms > shown) {
        elapsedRef.current = { ms, at: Date.now() };
      } else if (!cur) {
        elapsedRef.current = { ms: 0, at: Date.now() };
      }
    } else if (!elapsedRef.current) {
      elapsedRef.current = { ms: 0, at: Date.now() };
    }
    // eslint-disable-next-line
  }, [state.run?.elapsedMs, state.recording?.elapsedMs, phaseUi]);
  const elapsedMs = elapsedRef.current
    ? elapsedRef.current.ms + (Date.now() - elapsedRef.current.at)
    : 0;

  // Entering watchdog: never an infinite spinner.
  const [enterElapsed, setEnterElapsed] = useState(0);
  useEffect(() => {
    if (step !== "capture" || phaseUi !== "entering") {
      setEnterElapsed(0);
      return undefined;
    }
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      setEnterElapsed(n);
    }, 1000);
    return () => clearInterval(iv);
  }, [step, phaseUi, runId]);
  const enterStuck = enterElapsed >= 12;

  // Wipe elapsed (progress events are guaranteed but sparse).
  const [prepElapsed, setPrepElapsed] = useState(0);
  useEffect(() => {
    if (step !== "capture" || phaseUi !== "preparing") {
      setPrepElapsed(0);
      return undefined;
    }
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      setPrepElapsed(n);
    }, 1000);
    return () => clearInterval(iv);
  }, [step, phaseUi]);
  const prepSlow = prepElapsed >= 150;

  // ── Read stored records (no capture — the flash is the source) ─────
  const readResult = async () => {
    if (reading) return;
    setReading(true);
    setReadError(null);
    setReader(null);
    try {
      // Stop a live recording first — but only when the device is actually
      // in the mode. Sending a blind `centrifuge stop` from IDLE just earns
      // a nack, and the old flow turned that nack into a dead end.
      let liveStopped = null;
      if (inMode) {
        const stopStart = receivedRef.current.length;
        await sendCmd("centrifuge stop");
        await sleep(900);
        const rd = receivedRef.current;
        for (let i = stopStart; i < rd.length; i++) {
          const ev = parseFactoryLine(rd[i]?.data);
          if (ev?.t === "stopped") liveStopped = ev;
        }
      }

      // Read the persisted per-record peaks from flash.
      const dumpStart = receivedRef.current.length;
      await send("dump").catch(() => {});
      const deadline = Date.now() + 20000;
      let sawEnd = false;
      let started = false;
      let lastLen = receivedRef.current.length;
      while (Date.now() < deadline && !sawEnd && aliveRef.current) {
        await sleep(500);
        const rd = receivedRef.current;
        for (let i = dumpStart; i < rd.length; i++) {
          const d = String(rd[i]?.data);
          if (d.includes("[dump] end")) {
            sawEnd = true;
            break;
          }
          if (d.includes("[dump]")) started = true;
        }
        if (sawEnd) break;
        if (!started && rd.length === lastLen) await kick?.().catch?.(() => {});
        lastLen = rd.length;
      }
      const dumpLines = [];
      {
        const rd = receivedRef.current;
        for (let i = dumpStart; i < rd.length; i++) {
          dumpLines.push(String(rd[i]?.data));
        }
      }
      const parsed = parseDumpPeak(dumpLines);
      if (!sawEnd && parsed.records == null && parsed.peakG == null) {
        setReadError(t("deviceTools.centrifuge.errors.readNoAnswer"));
        return;
      }

      const peakG =
        parsed.peakG != null
          ? parsed.peakG
          : liveStopped?.peak_g != null
          ? liveStopped.peak_g
          : null;
      const records =
        parsed.records != null ? parsed.records : liveStopped?.records ?? null;

      if (peakG == null && (!records || records === 0)) {
        setReader({ empty: true });
      } else {
        setReader({
          peakG,
          records,
          fault: liveStopped?.fault === true,
          source: parsed.peakG != null ? "flash" : "stopped",
        });
      }
    } catch (err) {
      setReadError(
        formatApiError(err, t("deviceTools.centrifuge.errors.readFailed"))
      );
    } finally {
      setReading(false);
    }
  };

  const openRead = () => {
    setStep("read");
    readResult();
  };

  // ── Graph / save / import / log (unchanged mechanics) ──────────────
  const loadGraph = async () => {
    if (graphing) return [];
    setGraphing(true);
    setGraphError(null);
    setGraphRecords(null);
    try {
      const main = exportRecords ? await exportRecords() : null;
      const parsed = buildExportResult(main || {});
      const valid = (parsed.records || []).filter((r) => r.isValid);
      if (valid.length) {
        setGraphRecords(valid);
      } else if (!parsed.done && (parsed.records || []).length) {
        setGraphError(t("deviceTools.centrifuge.errors.exportPartial"));
      } else {
        setGraphError(t("deviceTools.centrifuge.errors.noDecodable"));
      }
      return valid;
    } catch (err) {
      setGraphError(
        formatApiError(err, t("deviceTools.centrifuge.errors.exportFailed"))
      );
      return [];
    } finally {
      setGraphing(false);
    }
  };

  const handleSaveFile = async () => {
    if (saveBusy || reading || graphing) return;
    setSaveBusy(true);
    try {
      let records = graphRecords;
      if (!records) records = await loadGraph();
      if (!records || !records.length) {
        showToast.error(t("deviceTools.centrifuge.errors.nothingExported"));
        return;
      }
      const { json } = buildCentrifugeFile({
        wearable,
        mac,
        fw: state.fw || deviceFw || null,
        peak: reader,
        note,
        records,
        exportedAt: new Date().toISOString(),
      });
      const defaultName = centrifugeFileName({
        wearable,
        mac,
        peakG: reader?.peakG,
        date: new Date(),
      });
      const res = await saveTextFile({ defaultName, contents: json });
      if (!res?.canceled)
        showToast.success(t("deviceTools.centrifuge.toast.runSaved"));
    } catch (err) {
      showToast.error(
        formatApiError(err, t("deviceTools.centrifuge.errors.saveFailed"))
      );
    } finally {
      setSaveBusy(false);
    }
  };

  const handleImport = async () => {
    try {
      const res = await openTextFile({
        filters: [
          {
            name: t("deviceTools.centrifuge.fileFilterName"),
            extensions: ["json"],
          },
        ],
      });
      if (res?.canceled) return;
      const parsed = parseCentrifugeFile(res.contents);
      setImported(parsed);
    } catch (err) {
      showToast.error(
        err?.message || t("deviceTools.centrifuge.errors.openFailed")
      );
    }
  };

  const logResult = async (result) => {
    if (!reader || reader.empty || saving) return;
    if (!mac) {
      setRegError(t("deviceTools.centrifuge.errors.noDevice"));
      return;
    }
    setSaving(true);
    setRegError(null);
    try {
      await betaAPI.createWearableAttempt({
        bleMac: mac,
        body: {
          sub_stage: "centrifuge",
          step: "centrifuge_test",
          result,
          data: {
            g: reader.peakG,
            records: reader.records,
            source: reader.source,
            fw: state.fw || deviceFw || null,
          },
          ...(note.trim() ? { notes: note.trim() } : {}),
        },
      });
      showToast.success(
        result === "pass"
          ? t("deviceTools.centrifuge.toast.passed")
          : t("deviceTools.centrifuge.toast.failedLogged")
      );
      exit();
    } catch (err) {
      setRegError(
        formatApiError(err, t("deviceTools.centrifuge.errors.logFailed"))
      );
    } finally {
      setSaving(false);
    }
  };

  const wipePct =
    state.wipe && state.wipe.total
      ? Math.min(100, Math.round((state.wipe.done / state.wipe.total) * 100))
      : null;

  const hasStored = (deviceRecords || 0) > 0;
  const canLog = wearable?.status === "in_validation" && !!mac;
  const liveBusy = phaseUi === "countdown" || phaseUi === "recording";

  const wide = imported
    ? !!(imported.records && imported.records.length)
    : step === "read" && (graphRecords || graphing || graphError);

  const handleBack = () => {
    if (imported) {
      setImported(null);
      return;
    }
    if (step === "capture" && liveBusy && !confirmExit) {
      setConfirmExit(true);
      return;
    }
    exit();
  };

  return (
    <div
      className={`mx-auto flex h-full flex-col ${
        wide ? "max-w-4xl" : "max-w-xl"
      }`}
    >
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151]"
          title={
            imported
              ? t("common.actions.back")
              : t("deviceTools.shared.backToDevice")
          }
        >
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827]">
            {imported
              ? t("deviceTools.centrifuge.imported")
              : t("deviceTools.centrifuge.title")}
          </p>
          <p className="truncate text-[11px] text-[#9CA3AF]">
            {imported
              ? `${wearableShortName(imported.wearable, null)}${
                  imported.exportedAt
                    ? ` · ${new Date(imported.exportedAt).toLocaleDateString()}`
                    : ""
                }`
              : `${
                  mac ? formatMac(mac) : t("deviceTools.shared.readingDevice")
                }${state.fw || deviceFw ? ` · ${state.fw || deviceFw}` : ""}`}
          </p>
        </div>
        {!imported && !connected && (
          <span className="flex items-center gap-1.5 rounded-full bg-[#FEF3E7] px-2.5 py-1 text-[11px] font-semibold text-[#B45309]">
            <Unplug size={12} />
            {t("deviceTools.centrifuge.unplugged")}
          </span>
        )}
      </div>

      {/* `safe center`: plain justify-center on an overflowing flex column
          pushes content above the scroll container's top edge, making the
          start of a tall result (peak + graph) unreachable by scrolling. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 [justify-content:safe_center]">
        {/* ── Imported run (read-only) ── */}
        {imported && (
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
            <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-[#DDEBFF] bg-[#F5F9FF] px-3 py-2">
              <FileClock
                size={16}
                className="mt-0.5 flex-shrink-0 text-[#2563EB]"
              />
              <div className="min-w-0 text-[12px] text-[#1E40AF]">
                <p className="font-medium">
                  {t("deviceTools.centrifuge.importedFrom", {
                    name: wearableShortName(imported.wearable, null),
                  })}
                </p>
                <p className="text-[#3B82F6]">
                  {imported.exportedAt
                    ? new Date(imported.exportedAt).toLocaleString()
                    : t("deviceTools.centrifuge.dateUnknown")}
                  {imported.firmware ? ` · ${imported.firmware}` : ""}
                </p>
              </div>
            </div>

            <div className="my-3 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-[#9CA3AF]">
                {t("deviceTools.centrifuge.peak")}
              </p>
              <p className="font-mono text-4xl font-bold text-[#111827]">
                {imported.peak?.peakG != null ? imported.peak.peakG : "—"}
                <span className="ml-1 text-xl text-[#6B7280]">g</span>
              </p>
              <p className="mt-1 text-[11px] text-[#9CA3AF]">
                {t("deviceTools.centrifuge.recordCount", {
                  count: imported.peak?.records ?? imported.records.length,
                })}
              </p>
            </div>

            {imported.note && (
              <p className="mb-3 rounded-lg bg-[#F9FAFB] px-3 py-2 text-[13px] text-[#374151]">
                {imported.note}
              </p>
            )}

            {imported.records.length ? (
              <WaveformGraph records={imported.records} />
            ) : (
              <p className="text-center text-[13px] text-[#9CA3AF]">
                {t("deviceTools.centrifuge.noWaveform")}
              </p>
            )}
          </div>
        )}

        {/* ── Home: capture is the invitation, never an error ── */}
        {!imported && step === "home" && (
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
            <div className="mb-4 flex items-center gap-2">
              <Disc3 size={18} className="flex-shrink-0 text-[#E07A3A]" />
              <p className="text-sm font-semibold text-[#111827]">
                {t("deviceTools.centrifuge.title")}
              </p>
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#F5F4F1] px-2.5 py-1 text-[11px] font-medium text-[#6B7280]">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    backgroundColor: hasStored ? "#15803D" : "#9CA3AF",
                  }}
                />
                {hasStored
                  ? t("deviceTools.centrifuge.recStored", {
                      count: deviceRecords,
                    })
                  : t("deviceTools.centrifuge.storeEmpty")}
              </span>
            </div>

            <button
              onClick={startCapture}
              disabled={!connected || commandsLocked}
              className="flex w-full items-center gap-3 rounded-[12px] px-4 py-3.5 text-left text-white transition duration-150 disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }}
            >
              <Disc3 size={20} className="flex-shrink-0" />
              <span>
                <span className="block text-sm font-semibold">
                  {t("deviceTools.centrifuge.captureNew")}
                </span>
                <span className="block text-[11px] text-white/80">
                  {t("deviceTools.centrifuge.captureNewHint")}
                </span>
              </span>
            </button>
            {hasStored && (
              <p className="mt-2 flex items-start gap-1.5 text-[11px] text-[#B45309]">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                {t("deviceTools.centrifuge.eraseWarning", {
                  count: deviceRecords,
                })}
              </p>
            )}

            {hasStored && (
              <button
                onClick={openRead}
                disabled={!connected || commandsLocked}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-[13px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
              >
                <Gauge size={14} className="text-[#E07A3A]" />
                {t("deviceTools.centrifuge.readStoredFirst")}
              </button>
            )}

            <button
              onClick={handleImport}
              className="mt-3 flex w-full items-center justify-center gap-1.5 text-[12px] font-medium text-[#6B7280] transition duration-150 hover:text-[#E07A3A]"
            >
              <Upload size={13} />
              {t("deviceTools.centrifuge.importSaved")}
            </button>
          </div>
        )}

        {/* ── Capture: one card, driven by the device's phase ── */}
        {!imported && step === "capture" && (
          <>
            {/* Exit confirm (countdown/recording only) */}
            {confirmExit && liveBusy && (
              <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4">
                <p className="text-sm font-semibold text-[#92400E]">
                  {phaseUi === "recording"
                    ? t("deviceTools.centrifuge.stillRecording")
                    : t("deviceTools.centrifuge.countdownRunning")}
                </p>
                <p className="mt-1 text-[13px] text-[#B45309]">
                  {t("deviceTools.centrifuge.leaveArmedHint")}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => exit({ keepArmed: true })}
                    className="flex items-center gap-1.5 rounded-[10px] px-4 py-2 text-sm font-semibold text-white"
                    style={{
                      background:
                        "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                    }}
                  >
                    <Unplug size={14} />
                    {phaseUi === "recording"
                      ? t("deviceTools.centrifuge.keepRecordingClose")
                      : t("deviceTools.centrifuge.keepArmedClose")}
                  </button>
                  <button onClick={() => exit()} className={btnSecondary}>
                    {t("deviceTools.centrifuge.stopExit")}
                  </button>
                  <button
                    onClick={() => setConfirmExit(false)}
                    className="text-[12px] font-medium text-[#92400E] hover:underline"
                  >
                    {t("deviceTools.centrifuge.stay")}
                  </button>
                </div>
              </div>
            )}

            {/* nack (wrong state) */}
            {phaseUi === "nack" && (
              <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-5 text-center">
                <XCircle size={26} className="mx-auto text-[#DC2626]" />
                <p className="mt-2 text-sm font-medium text-[#B91C1C]">
                  {state.nack?.reason === "wrong_state"
                    ? state.nack?.state
                      ? t("deviceTools.centrifuge.cantStartFromState", {
                          state: state.nack.state,
                        })
                      : t("deviceTools.centrifuge.cantStartFromThisState")
                    : state.nack?.reason
                    ? t("deviceTools.centrifuge.commandRefused", {
                        reason: state.nack.reason,
                      })
                    : t("deviceTools.centrifuge.commandRefusedUnknown")}
                </p>
                <p className="mt-1 text-xs text-[#DC2626]">
                  {t("deviceTools.centrifuge.mustBeIdle")}
                </p>
                <button
                  onClick={() => setStep("home")}
                  className={`mt-3 ${btnSecondary}`}
                >
                  {t("common.actions.back")}
                </button>
              </div>
            )}

            {/* error (incl. fatal prepare_failed) */}
            {phaseUi === "error" && (
              <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-5 text-center">
                <XCircle size={26} className="mx-auto text-[#DC2626]" />
                <p className="mt-2 text-sm font-medium text-[#B91C1C]">
                  {state.prepFailed
                    ? t("deviceTools.centrifuge.prepareFailed")
                    : state.error?.detail ||
                      state.error?.reason ||
                      t("deviceTools.centrifuge.captureError")}
                </p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setStep("home")}
                    className={btnSecondary}
                  >
                    {t("common.actions.back")}
                  </button>
                  <button
                    onClick={startCapture}
                    className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <RotateCw size={14} />
                    {t("deviceTools.centrifuge.reEnter")}
                  </button>
                </div>
              </div>
            )}

            {/* entering (with watchdog) */}
            {phaseUi === "entering" && (
              <div className="rounded-xl border border-[#F3F4F6] bg-white p-5 text-center">
                <Loader2
                  size={24}
                  className="mx-auto animate-spin text-[#E07A3A]"
                />
                <p className="mt-2 text-sm font-medium text-[#111827]">
                  {t("deviceTools.centrifuge.entering")}
                </p>
                {enterStuck && (
                  <div className="mt-3 border-t border-[#F3F4F6] pt-3">
                    <p className="text-xs text-[#9CA3AF]">
                      {t("deviceTools.centrifuge.noAnswer")}
                    </p>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <button
                        onClick={() => setStep("home")}
                        className={btnSecondary}
                      >
                        {t("common.actions.back")}
                      </button>
                      <button
                        onClick={startCapture}
                        className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                      >
                        <RotateCw size={14} />
                        {t("common.actions.retry")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* preparing (wipe) */}
            {phaseUi === "preparing" && (
              <div className="rounded-xl border border-[#F3F4F6] bg-white p-5 text-center">
                <Loader2
                  size={22}
                  className="mx-auto animate-spin text-[#E07A3A]"
                />
                <p className="mt-2 text-sm font-medium text-[#111827]">
                  {t("deviceTools.centrifuge.erasing")}
                </p>
                <p className="mx-auto max-w-xs text-xs text-[#9CA3AF]">
                  {t("deviceTools.centrifuge.erasingHint")}
                </p>
                <div className="mt-3">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                    <div
                      className="h-full rounded-full bg-[#E07A3A] transition-all"
                      style={{ width: `${wipePct ?? 4}%` }}
                    />
                  </div>
                  <p className="mt-1 font-mono text-[11px] text-[#9CA3AF]">
                    {wipePct != null
                      ? t("deviceTools.centrifuge.elapsedWithPct", {
                          pct: wipePct,
                          seconds: prepElapsed,
                        })
                      : t("deviceTools.centrifuge.elapsed", {
                          seconds: prepElapsed,
                        })}
                  </p>
                </div>
                {prepSlow && (
                  <div className="mt-4 border-t border-[#F3F4F6] pt-3">
                    <p className="text-xs text-[#9CA3AF]">
                      {t("deviceTools.centrifuge.slowPrepare")}
                    </p>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <button onClick={() => exit()} className={btnSecondary}>
                        {t("common.actions.back")}
                      </button>
                      <button
                        onClick={startCapture}
                        className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-3.5 py-1.5 text-xs font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                      >
                        <RotateCw size={13} />
                        {t("deviceTools.centrifuge.reEnter")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* waiting — ready to arm */}
            {phaseUi === "waiting" && (
              <div className="rounded-xl border border-[#F3F4F6] bg-white p-5 text-center">
                <CheckCircle2 size={26} className="mx-auto text-[#16A34A]" />
                <p className="mt-2 text-sm font-medium text-[#111827]">
                  {t("deviceTools.centrifuge.readyToRecord")}
                </p>
                <p className="mx-auto mt-1 max-w-xs text-[13px] text-[#6B7280]">
                  {state.capacity?.ms != null
                    ? t("deviceTools.centrifuge.armHintCapacity", {
                        minutes: Math.round(state.capacity.ms / 60000),
                      })
                    : t("deviceTools.centrifuge.armHint")}
                </p>

                {cent2 && (
                  <div className="mt-4 flex items-center justify-center gap-1.5">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                      {t("deviceTools.centrifuge.countdown")}
                    </span>
                    {ARM_CHOICES.map((secs) => (
                      <button
                        key={secs}
                        onClick={() => setArmSecs(secs)}
                        className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition duration-150 ${
                          armSecs === secs
                            ? "border-[#E07A3A]/40 bg-[#FFF3ED] text-[#E07A3A]"
                            : "border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB]"
                        }`}
                      >
                        {t("deviceTools.centrifuge.armSeconds", {
                          seconds: secs,
                        })}
                      </button>
                    ))}
                  </div>
                )}

                {armStuck && (
                  <p className="mt-3 text-xs text-[#DC2626]">
                    {t("deviceTools.centrifuge.armNotConfirmed")}
                  </p>
                )}
                {state.ignored?.reason === "prepare_failed" && (
                  <p className="mt-3 text-xs text-[#DC2626]">
                    {t("deviceTools.centrifuge.armRefused")}
                  </p>
                )}

                <button
                  onClick={arm}
                  disabled={!connected || armPending}
                  className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                  style={{
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }}
                >
                  {armPending ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      {t("deviceTools.centrifuge.arming")}
                    </>
                  ) : (
                    <>
                      <Timer size={15} />
                      {t("deviceTools.centrifuge.arm", {
                        seconds: cent2 ? armSecs : Math.round(armMs / 1000),
                      })}
                    </>
                  )}
                </button>
                <p className="mt-2 text-[11px] text-[#9CA3AF]">
                  {t("deviceTools.centrifuge.deviceButtonHint")}
                </p>
              </div>
            )}

            {/* countdown — big numeral, cancellable */}
            {phaseUi === "countdown" && (
              <div className="rounded-xl border border-[#FEEAD9] bg-[#FFF9F4] p-6 text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#B45309]">
                  {t("deviceTools.centrifuge.recordingStartsIn")}
                </p>
                <p className="mt-1 font-mono text-6xl font-bold tabular-nums text-[#111827]">
                  {Math.ceil(remainMs / 1000)}
                  <span className="ml-1 text-2xl text-[#9CA3AF]">s</span>
                </p>
                <div className="mx-auto mt-3 h-1.5 w-56 overflow-hidden rounded-full bg-[#F5E9DD]">
                  <div
                    className="h-full rounded-full bg-[#E07A3A]"
                    style={{
                      width: `${
                        armMs > 0
                          ? Math.max(2, 100 - (remainMs / armMs) * 100)
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <p className="mx-auto mt-3 max-w-xs text-[13px] text-[#6B7280]">
                  {t("deviceTools.centrifuge.mountNow")}
                </p>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button onClick={cancelCountdown} className={btnSecondary}>
                    {t("deviceTools.centrifuge.cancelCountdown")}
                  </button>
                  <button
                    onClick={() => exit({ keepArmed: true })}
                    className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <Unplug size={14} />
                    {t("deviceTools.centrifuge.closeSpinUnplugged")}
                  </button>
                </div>
              </div>
            )}

            {/* recording — live */}
            {phaseUi === "recording" && (
              <div className="rounded-xl border border-[#F3F4F6] bg-white p-6">
                <div className="flex items-center justify-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#DC2626] opacity-60" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#DC2626]" />
                  </span>
                  <p className="text-sm font-semibold text-[#111827]">
                    {t("deviceTools.centrifuge.recording")}
                  </p>
                </div>
                <p className="mt-2 text-center font-mono text-5xl font-bold tabular-nums text-[#111827]">
                  {fmtClock(elapsedMs)}
                </p>
                <div className="mx-auto mt-4 grid max-w-sm grid-cols-2 gap-3 text-center">
                  <div className="rounded-lg bg-[#F9FAFB] px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                      {t("deviceTools.centrifuge.records")}
                    </p>
                    <p className="font-mono text-[15px] font-semibold tabular-nums text-[#111827]">
                      {/* Tick first: the snapshot's run_records is 0 at the
                          recording edge and never updates mid-run. */}
                      {state.recording?.records ?? state.run?.records ?? "—"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-[#F9FAFB] px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                      {t("deviceTools.centrifuge.spaceLeft")}
                    </p>
                    <p className="font-mono text-[15px] font-semibold tabular-nums text-[#111827]">
                      {state.recording?.leftRecords != null
                        ? t("deviceTools.centrifuge.minutes", {
                            minutes: Math.round(
                              (state.recording.leftRecords * 3000) / 60000
                            ),
                          })
                        : "—"}
                    </p>
                  </div>
                </div>
                {connected ? (
                  <p className="mx-auto mt-3 max-w-xs text-center text-[12px] text-[#9CA3AF]">
                    {t("deviceTools.centrifuge.unplugSafe")}
                  </p>
                ) : (
                  <p className="mx-auto mt-3 max-w-xs text-center text-[12px] font-medium text-[#B45309]">
                    {t("deviceTools.centrifuge.unpluggedRecording")}
                  </p>
                )}
                <button
                  onClick={finishRun}
                  disabled={!connected || finishing}
                  className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                  style={{
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }}
                >
                  {finishing ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      {t("deviceTools.centrifuge.finishing")}
                    </>
                  ) : (
                    <>
                      <Square size={14} />
                      {t("deviceTools.centrifuge.finishRead")}
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Result (live Finish and flash reads land here) ── */}
        {!imported && step === "read" && (
          <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
            {reading ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <Loader2 size={24} className="animate-spin text-[#E07A3A]" />
                <p className="text-sm text-[#6B7280]">
                  {t("deviceTools.centrifuge.readingRecording")}
                </p>
              </div>
            ) : readError ? (
              <div className="text-center">
                <XCircle size={26} className="mx-auto text-[#DC2626]" />
                <p className="mt-2 text-sm text-[#B91C1C]">{readError}</p>
                <div className="mt-3 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setStep("home")}
                    className={btnSecondary}
                  >
                    {t("common.actions.back")}
                  </button>
                  <button
                    onClick={readResult}
                    className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <RotateCw size={14} />
                    {t("common.actions.retry")}
                  </button>
                </div>
              </div>
            ) : reader?.empty ? (
              <div className="text-center">
                <AlertTriangle size={26} className="mx-auto text-[#E07A3A]" />
                <p className="mt-2 text-sm font-medium text-[#111827]">
                  {t("deviceTools.centrifuge.nothingStored")}
                </p>
                <p className="mt-1 text-[13px] text-[#6B7280]">
                  {t("deviceTools.centrifuge.storeEmptyHint")}
                </p>
                <button
                  onClick={startCapture}
                  disabled={!connected || commandsLocked}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                  style={{
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }}
                >
                  <Disc3 size={15} />
                  {t("deviceTools.centrifuge.captureNew")}
                </button>
              </div>
            ) : reader ? (
              <>
                <div className="my-3 text-center">
                  <p className="text-xs font-medium uppercase tracking-wide text-[#9CA3AF]">
                    {t("deviceTools.centrifuge.peak")}
                  </p>
                  <p className="font-mono text-4xl font-bold text-[#111827]">
                    {reader.peakG != null
                      ? Math.round(reader.peakG * 10) / 10
                      : "—"}
                    <span className="ml-1 text-xl text-[#6B7280]">g</span>
                  </p>
                  <p className="mt-1 text-[11px] text-[#9CA3AF]">
                    {reader.source === "flash"
                      ? t("deviceTools.centrifuge.recordCountFlash", {
                          count: reader.records ?? 0,
                        })
                      : t("deviceTools.centrifuge.recordCountLive", {
                          count: reader.records ?? 0,
                        })}
                  </p>
                  {reader.fault && (
                    <p className="mx-auto mt-2 flex max-w-xs items-start justify-center gap-1.5 rounded-lg bg-[#FEF2F2] px-3 py-2 text-[12px] font-medium text-[#DC2626]">
                      <AlertTriangle
                        size={13}
                        className="mt-0.5 flex-shrink-0"
                      />
                      {t("deviceTools.centrifuge.faultWarning")}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {!graphRecords && (
                    <button
                      onClick={loadGraph}
                      disabled={graphing || saveBusy}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
                    >
                      {graphing ? (
                        <>
                          <Loader2 size={13} className="animate-spin" />
                          {exportTotal > 0 && exportReceived > 0
                            ? t("deviceTools.centrifuge.exportingProgress", {
                                pct: Math.min(
                                  100,
                                  Math.round(
                                    (exportReceived / exportTotal) * 100
                                  )
                                ),
                                received: exportReceived,
                                total: exportTotal,
                              })
                            : t("deviceTools.centrifuge.exporting")}
                        </>
                      ) : (
                        <>
                          <Activity size={13} className="text-[#E07A3A]" />
                          {t("deviceTools.centrifuge.graphRecording")}
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={handleSaveFile}
                    disabled={saveBusy || graphing}
                    title={t("deviceTools.centrifuge.saveToFileTitle")}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
                  >
                    {saveBusy ? (
                      <>
                        <Loader2 size={13} className="animate-spin" />
                        {t("common.status.saving")}
                      </>
                    ) : (
                      <>
                        <Download size={13} className="text-[#E07A3A]" />
                        {t("deviceTools.centrifuge.saveToFile")}
                      </>
                    )}
                  </button>
                </div>
                {graphing && exportTotal > 0 && (
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                    <div
                      className="h-full rounded-full bg-[#E07A3A] transition-all duration-300"
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round(
                            ((exportReceived || 0) / exportTotal) * 100
                          )
                        )}%`,
                      }}
                    />
                  </div>
                )}
                {graphError && (
                  <p className="mt-2 text-xs text-[#DC2626]">{graphError}</p>
                )}
                {graphRecords && (
                  <div className="mt-3">
                    <WaveformGraph records={graphRecords} />
                  </div>
                )}

                {canLog ? (
                  <div className="mt-4 border-t border-[#F3F4F6] pt-3">
                    <button
                      onClick={() => setShowLog((v) => !v)}
                      className="flex w-full items-center gap-1.5 text-[12px] font-semibold text-[#6B7280] transition duration-150 hover:text-[#374151]"
                    >
                      {showLog ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      {t("deviceTools.centrifuge.logToChecklist")}
                      <span className="ml-auto rounded bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-semibold text-[#6366F1]">
                        {t("deviceTools.centrifuge.centrifugeStep")}
                      </span>
                    </button>
                    {showLog && (
                      <div className="mt-2.5">
                        <input
                          type="text"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder={t(
                            "deviceTools.centrifuge.notePlaceholder"
                          )}
                          className="w-full rounded-[10px] border border-[#E5E7EB] px-3 py-2 text-[13px] text-[#111827] outline-none focus:border-[#E07A3A]"
                        />
                        {regError && (
                          <p className="mt-2 text-xs text-[#DC2626]">
                            {regError}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => logResult("pass")}
                            disabled={saving}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                            style={{
                              background:
                                "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
                            }}
                          >
                            {saving ? (
                              <Loader2 size={15} className="animate-spin" />
                            ) : (
                              <Check size={15} />
                            )}
                            {t("deviceTools.centrifuge.pass")}
                          </button>
                          <button
                            onClick={() => logResult("fail")}
                            disabled={saving}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border border-[#FCA5A5] bg-white px-4 py-2.5 text-sm font-semibold text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2] disabled:opacity-50"
                          >
                            <X size={15} />
                            {t("deviceTools.centrifuge.fail")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="mt-3 flex items-center justify-between gap-2 border-t border-[#F3F4F6] pt-3">
                  <button
                    onClick={
                      // Run again in-mode appends to the store; once an
                      // export ran, the device already left the mode.
                      graphRecords || !inMode
                        ? startCapture
                        : () => {
                            // Clear the old verdict so the results effect
                            // doesn't bounce straight back here.
                            setState((prev) => ({
                              ...prev,
                              stopped: null,
                              lastRun: null,
                            }));
                            setReader(null);
                            setStep("capture");
                          }
                    }
                    disabled={!connected || commandsLocked}
                    className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
                  >
                    <Disc3 size={13} className="text-[#E07A3A]" />
                    {graphRecords || !inMode
                      ? t("deviceTools.centrifuge.newCapture")
                      : t("deviceTools.centrifuge.runAgain")}
                  </button>
                  <button onClick={() => exit()} className={btnSecondary}>
                    {t("common.actions.done")}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default CentrifugeFlow;
